/**
 * Pure functions that calculate wizard tab completion state from API data.
 * Used by EditModeCompletionInitializer to compute completion state
 * for all tabs without requiring each tab to mount.
 *
 * Each calculator replicates the exact same completion logic as its
 * corresponding tab component.
 */

// Helper to check if a related work entry is non-empty
export function hasNonEmptyRelatedWork(work: string | { catalogItemId: string; title: string }): boolean {
  return typeof work === 'string' ? work.trim() !== '' : Boolean(work?.catalogItemId);
}

interface UploadData {
  images: Array<{ imageId: string; status?: string }>;
  coverImageId: string | null | undefined;
}

interface DescribeData {
  creators?: Array<{ name: string; isCurrentUser: boolean }>;
  title?: string;
  signatures?: Array<{ type: string | string[]; location: string | string[]; display: string }>;
  creationDate?: { type: string; value: string; location: string | string[]; display: string };
}

interface MediaData {
  workType?: { id: string; label: string; path: unknown[] } | null;
  techniques?: Array<{ id: string; label: string; path: unknown[] }>;
  materials?: Array<{ id: string; label: string; path: unknown[] }>;
  substrate?: Array<{ id: string; label: string; path: unknown[] }>;
}

interface MeasureData {
  dimensions?: Array<{ type: string; value: string; fraction: string }>;
}

interface CatalogKeywordsData {
  aliCatalogNumber?: string;
  customCatalogNumbers?: string[];
  keywords?: string[];
  collectionIds?: string[];
}

interface ProvenanceData {
  provenanceRecords?: Array<{ publicRendering: string; internalRendering: string }>;
  exhibitions?: Array<{
    type: string;
    exhibitionType?: string;
    publicationText?: string;
  }>;
  // Related Works lives in `additionalInfo.*` storage but is tracked under the
  // provenance tab (step 3) after #1082. `calculateAllTabsCompletion` merges
  // the value through from the additionalInfo payload.
  relatedWorks?: Array<string | { catalogItemId: string; title: string }>;
}

interface AdditionalInfoData {
  // Still present on the AdditionalInfo API response shape, but no longer used
  // by `calculateAdditionalInfoCompletion` — moved to provenance (#1082).
  relatedWorks?: Array<string | { catalogItemId: string; title: string }>;
  publicNotes?: string[];
  privateNotes?: string[];
  physicalLocation?: string;
  estimatedValue?: string;
}

// Matches UploadTab completion logic (lines ~216-219)
export function calculateUploadCompletion(data: UploadData): Record<number, boolean> {
  return {
    0: data.images.length > 0,
    1: data.coverImageId !== null && data.coverImageId !== undefined
  };
}

// Matches DescribeTab.updateCompletionTracking (4 sections after media split)
export function calculateDescribeCompletion(data: DescribeData): Record<number, boolean> {
  const creators = data.creators || [];
  const hasCreator = creators.some(c => c.name.trim() !== '');
  const hasTitle = (data.title || '').trim() !== '';
  const signatures = data.signatures || [];
  const hasSignature = signatures.length > 0 &&
    signatures.some(s =>
      (Array.isArray(s.type) ? s.type.length > 0 : Boolean(s.type)) ||
      (Array.isArray(s.location) ? s.location.length > 0 : Boolean(s.location)) ||
      s.display.trim()
    );
  const hasCreationDate = (data.creationDate?.value || '') !== '';

  return {
    0: hasCreator,
    1: hasTitle,
    2: hasSignature,
    3: hasCreationDate
  };
}

// Matches MediaTab completion tracking (4 sections: workType, techniques, materials, substrate)
export function calculateMediaCompletion(data: MediaData): Record<number, boolean> {
  const hasWorkType = data.workType !== null && data.workType !== undefined;
  const hasTechniques = (data.techniques || []).length > 0;
  const hasMaterials = (data.materials || []).length > 0;
  const hasSubstrate = (data.substrate || []).length > 0;

  return {
    0: hasWorkType,
    1: hasTechniques,
    2: hasMaterials,
    3: hasSubstrate
  };
}

// Matches MeasureTab completion logic (line ~76)
export function calculateMeasureCompletion(data: MeasureData): Record<number, boolean> {
  return {
    0: (data.dimensions || []).length > 0
  };
}

// Matches CatalogKeywordsTab completion logic (lines ~58-61, ~93-97)
export function calculateCatalogKeywordsCompletion(data: CatalogKeywordsData): Record<number, boolean> {
  const hasCatalogNumber = (data.aliCatalogNumber || '').trim() !== '' ||
    (data.customCatalogNumbers || []).length > 0;
  const hasKeywords = (data.keywords || []).length > 0;
  const hasCollections = (data.collectionIds || []).length > 0;

  return {
    0: hasCatalogNumber,
    1: hasKeywords,
    2: hasCollections
  };
}

// Matches ProvenanceTab completion logic.
// Steps after #1082: 0 = records, 1 = exhibitions, 2 = publications, 3 = related works.
export function calculateProvenanceCompletion(data: ProvenanceData): Record<number, boolean> {
  const records = data.provenanceRecords || [];
  const hasRecords = records.some(r => r.publicRendering.trim() || r.internalRendering.trim());

  const allExhibitions = data.exhibitions || [];
  const exhibitionEntries = allExhibitions.filter(e => e.type === 'exhibition');
  const publicationEntries = allExhibitions.filter(e => e.type === 'publication');

  const hasExhibitions = exhibitionEntries.some(
    e => (e.exhibitionType || '').trim()
  );
  const hasPublications = publicationEntries.some(
    p => (p.publicationText || '').trim()
  );

  const relatedWorks = data.relatedWorks || [];
  const hasRelatedWorks = relatedWorks.some(hasNonEmptyRelatedWork);

  return {
    0: hasRecords,
    1: hasExhibitions,
    2: hasPublications,
    3: hasRelatedWorks
  };
}

// Matches AdditionalInfoTab completion logic.
// Steps after #1082: 0 = notes (public/private), 1 = location and value.
// Related Works moved to provenance[3].
export function calculateAdditionalInfoCompletion(data: AdditionalInfoData): Record<number, boolean> {
  const hasPublicNotes = (data.publicNotes || []).some(note => note.trim() !== '');
  const hasPrivateNotes = (data.privateNotes || []).some(note => note.trim() !== '');
  const hasLocationOrValue = (data.physicalLocation || '').trim() !== '' ||
    (data.estimatedValue || '').trim() !== '';

  return {
    0: hasPublicNotes || hasPrivateNotes,
    1: hasLocationOrValue
  };
}

interface AllTabsData {
  upload: UploadData;
  describe: DescribeData;
  media: MediaData;
  measure: MeasureData;
  catalogKeywords: CatalogKeywordsData;
  provenance: ProvenanceData;
  additionalInfo: AdditionalInfoData;
}

export function calculateAllTabsCompletion(data: AllTabsData): Record<string, Record<number, boolean>> {
  // Related Works is persisted under `additionalInfo.relatedWorks` but tracked
  // against the provenance tab (#1082) — merge it into the provenance calculation.
  const provenanceWithRelated: ProvenanceData = {
    ...data.provenance,
    relatedWorks: data.additionalInfo.relatedWorks
  };

  return {
    upload: calculateUploadCompletion(data.upload),
    describe: calculateDescribeCompletion(data.describe),
    media: calculateMediaCompletion(data.media),
    measure: calculateMeasureCompletion(data.measure),
    catalogKeywords: calculateCatalogKeywordsCompletion(data.catalogKeywords),
    provenance: calculateProvenanceCompletion(provenanceWithRelated),
    additionalInfo: calculateAdditionalInfoCompletion(data.additionalInfo)
  };
}
